import React from "react";
import UntaggedGroups from "../UntaggedGroups/UntaggedGroups";
import type { StagedZone } from "../UntaggedGroups/UntaggedGroups";
import type { ActivityMove, CodeEdit } from "../../state";
import type { SectionMeta, TreeActivity } from "../../tree";
import type { ExistingWorkCode } from "../../../../../models";
import "./NotCodedBucket.css";

interface LevelGroup {
  takt_b: string;
  takt_c: string;
  activities: TreeActivity[];
}

interface Props {
  /** The section's un-coded activities, grouped by level (board order). */
  groups: LevelGroup[];
  /** Render one activity as the section's standard ActivityRow (click it to
   *  assign a code in place). */
  renderActivity: (a: TreeActivity) => React.ReactNode;
  // ---- passthrough to UntaggedGroups for the opt-in "Suggest" view ----
  scheduleId: number;
  sectionName: string;
  sessionCodes?: ExistingWorkCode[];
  sectionOptions: string[];
  getSectionMeta: (section: string) => SectionMeta;
  onNeedSection: (section: string) => void;
  onMoveToBoard: (moves: ActivityMove[]) => void;
  colors: Record<string, string>;
  codeEdits: Record<string, CodeEdit>;
  stagedZones?: StagedZone[];
  excludedIds?: number[];
}

/** The section's "Not coded" rail. Default = a plain list of the section's
 *  un-coded activities under little per-level headers (click a row to code it).
 *  ONE "Suggest" opts into the name-grouped + matched-code view — grouping by
 *  NAME across the whole section is the point (same work in every level lands on
 *  one code, and you can see they're all together). Untagging more work into the
 *  rail (count grows) drops back to the list so a suggestion doesn't churn. */
const NotCodedBucket: React.FC<Props> = ({ groups, renderActivity, ...ug }) => {
  const flat = React.useMemo(() => groups.flatMap((g) => g.activities), [groups]);
  const [suggesting, setSuggesting] = React.useState(false);

  const prevCount = React.useRef(flat.length);
  React.useEffect(() => {
    if (flat.length > prevCount.current) setSuggesting(false);
    prevCount.current = flat.length;
  }, [flat.length]);

  return (
    <div className="takt-notcoded">
      <div className="takt-notcoded-head">
        <span className="takt-notcoded-title">Not coded</span>
        <span className="takt-notcoded-count">{flat.length}</span>
        <button
          type="button"
          className={`takt-notcoded-suggest ${suggesting ? "is-on" : ""}`}
          onClick={() => setSuggesting((s) => !s)}
          title={
            suggesting
              ? "Back to the plain list"
              : "Group by name across the section and pre-fill a matched work code"
          }
        >
          {suggesting ? "← List" : "⚡ Suggest"}
        </button>
      </div>

      {suggesting ? (
        <UntaggedGroups
          scheduleId={ug.scheduleId}
          sectionName={ug.sectionName}
          untagged={flat}
          sessionCodes={ug.sessionCodes}
          sectionOptions={ug.sectionOptions}
          getSectionMeta={ug.getSectionMeta}
          onNeedSection={ug.onNeedSection}
          onMoveToBoard={ug.onMoveToBoard}
          colors={ug.colors}
          codeEdits={ug.codeEdits}
          stagedZones={ug.stagedZones}
          excludedIds={ug.excludedIds}
          codeOnly
        />
      ) : (
        <div className="takt-notcoded-list">
          {groups.map((g) => (
            <div key={`${g.takt_b} ${g.takt_c}`} className="takt-notcoded-level">
              <div className="takt-notcoded-level-head">
                {[g.takt_b, g.takt_c].filter(Boolean).join(" · ") || "—"}
              </div>
              {g.activities.map(renderActivity)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotCodedBucket;
